import styled, { css } from 'styled-components';
import Badge from "../../basic/Badge/Badge.js";
import EmptyState from "../../basic/EmptyState/EmptyState.js";
import Layout from "../../basic/Layout/Layout.js";
import { getDataColumnPadding, getRowColumnPadding, getSubItemIndent, getTablePadding } from "./helpers.js";
/* Styled table components */

/*
  Each column has three sections;
  Name: Main left column
  Data: A column of columns
  Action: Rightmost button area
*/

export var InnerTableContainer = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-0"
})(["width:100%;margin:0;max-width:100%;overflow:auto;opacity:", ";padding:0 ", ";transition:opacity 500ms;"], _ref => {
  var {
    isLoading
  } = _ref;
  return isLoading ? '0' : '1';
}, _ref2 => {
  var {
    size
  } = _ref2;
  return size && getTablePadding(size).horizontal;
});
export var TableElement = styled.table.withConfig({
  componentId: "CL__sc-l97mb9-1"
})(["border-collapse:collapse;height:100%;width:100%;max-width:100%;"]);

/* Components above table */

export var TableControls = styled(Layout).withConfig({
  componentId: "CL__sc-l97mb9-2"
})(["opacity:", ";padding:0 ", ";transition:opacity 500ms;@media only screen and (max-width:767px){&{flex-direction:column;align-items:flex-start;& > *:not(:last-child),& > *[class]:not(:last-child){margin:0 0 var(--layout-gap);}}}"], _ref3 => {
  var {
    isLoading
  } = _ref3;
  return isLoading ? '0' : '1';
}, _ref4 => {
  var {
    size
  } = _ref4;
  return size && "".concat(getTablePadding(size).horizontal, " ").concat(getTablePadding(size).vertical);
});
export var TableLoadingState = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-3"
})(["display:flex;align-items:center;justify-content:center;position:absolute;top:0;z-index:10;height:100%;width:100%;text-align:center;padding:", ";"], _ref5 => {
  var {
    size
  } = _ref5;
  return size && "0 ".concat(getTablePadding(size).horizontal);
});
export var TableEmptyState = styled(EmptyState).withConfig({
  componentId: "CL__sc-l97mb9-4"
})(["padding:", ";"], _ref6 => {
  var {
    size
  } = _ref6;
  return size && "0 ".concat(getTablePadding(size).horizontal);
});
export var TableSearch = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-5"
})(["width:100%;max-width:240px;"]);
export var TableHeader = styled.thead.withConfig({
  componentId: "CL__sc-l97mb9-6"
})(["font:var(--body-text-", ");font-weight:600;"], _ref7 => {
  var {
    size
  } = _ref7;
  return size;
});
export var TableHeaderInner = styled.tr.withConfig({
  componentId: "CL__sc-l97mb9-7"
})([""]);
var getFlexAlignment = align => {
  if (align === 'left') {
    return 'flex-start';
  }
  if (align === 'center') {
    return 'center';
  }
  if (align === 'right') {
    return 'flex-end';
  }
};
export var ColumnHeaderText = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-8"
})(["display:flex;justify-content:", ";color:var(--text-color);text-overflow:ellipsis;white-space:nowrap;overflow:hidden;.column-header-tooltip-icon{cursor:pointer;margin-left:3px;&:hover{color:var(--mint-core);}}"], _ref8 => {
  var {
    align
  } = _ref8;
  return getFlexAlignment(align || 'left');
});
export var ColumnHeaderBadge = styled(Badge).withConfig({
  componentId: "CL__sc-l97mb9-9"
})(["margin-left:var(--spacing-1);vertical-align:middle;"]);

/* Components below table */

export var TablePagination = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-10"
})(["opacity:", ";padding:", ";transition:opacity 500ms;"], _ref9 => {
  var {
    isLoading
  } = _ref9;
  return isLoading ? '0' : '1';
}, _ref10 => {
  var {
    size
  } = _ref10;
  return size && "".concat(getTablePadding(size).horizontal, " ").concat(getTablePadding(size).vertical, " 0");
});

/* Table header and item columns */

export var TableItemCheckbox = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-11"
})(["padding:", " 6px 0;width:", ";", ""], _ref11 => {
  var {
    size
  } = _ref11;
  return size === 'medium' ? '0' : '3px';
}, _ref12 => {
  var {
    size
  } = _ref12;
  return size === 'small' ? '27px' : '36px';
}, _ref13 => {
  var {
    inHeader,
    size
  } = _ref13;
  return inHeader && css(["left:", ";position:absolute;& + ", "{margin-left:", ";}"], getRowColumnPadding(size), ColumnHeaderText, size === 'small' ? '27px' : '36px');
});
export var TableItemExpandButton = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-12"
})(["padding:", " 6px 0 0;> button{transition:transform var(--hover-transition);transform:", ";}"], _ref14 => {
  var {
    size
  } = _ref14;
  return size === 'large' ? '3px' : '0';
}, _ref15 => {
  var {
    expanded
  } = _ref15;
  return expanded ? 'rotate(180deg)' : '';
});
export var TableItemCustomIcon = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-13"
})(["text-align:center;padding-top:", ";width:27px;"], _ref16 => {
  var {
    size
  } = _ref16;
  return size === 'large' ? '6px' : '3px';
});
export var NameColumn = styled.td.withConfig({
  componentId: "CL__sc-l97mb9-14"
})(["display:flex;height:100%;position:relative;flex:1 0 auto;min-width:", ";padding:", ";", ";text-align:left;"], _ref17 => {
  var {
    minWidth
  } = _ref17;
  return minWidth ? "".concat(minWidth, "px;") : '120px';
}, _ref18 => {
  var {
    size
  } = _ref18;
  return getRowColumnPadding(size);
}, _ref19 => {
  var {
    hasParentItem,
    size
  } = _ref19;
  return hasParentItem ? "padding-left: ".concat(getSubItemIndent(size)) : '';
});
export var DataColumnContainer = styled.td.withConfig({
  componentId: "CL__sc-l97mb9-15"
})(["flex:0 1 100%;height:100%;padding:", ";> div{display:grid;grid-auto-flow:column;grid-auto-columns:1fr;min-width:100%;}"], _ref20 => {
  var {
    size
  } = _ref20;
  return getRowColumnPadding(size);
});
export var DataColumn = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-16"
})(["text-align:", ";min-width:", ";padding:", ";text-overflow:ellipsis;"], _ref21 => {
  var {
    align
  } = _ref21;
  return align || 'left';
}, _ref22 => {
  var {
    minWidth
  } = _ref22;
  return minWidth ? "".concat(minWidth, "px;") : '84px';
}, _ref23 => {
  var {
    size
  } = _ref23;
  return getDataColumnPadding(size);
});
export var ActionColumn = styled.td.withConfig({
  componentId: "CL__sc-l97mb9-17"
})(["flex:1 0 auto;height:100%;text-align:", ";min-width:", ";padding:", ";"], _ref24 => {
  var {
    align
  } = _ref24;
  return align;
}, _ref25 => {
  var {
    minWidth
  } = _ref25;
  return minWidth ? "".concat(minWidth, "px;") : '60px';
}, _ref26 => {
  var {
    size
  } = _ref26;
  return getRowColumnPadding(size);
});
export var StyledTableItem = styled.tr.withConfig({
  componentId: "CL__sc-l97mb9-18"
})(["--table-row-selected-color:var(--blue-1);[data-theme='dark'] &{--table-row-selected-color:var(--purple-7);}border-top:solid 1px var(--border-color-dim);position:relative;margin:0;padding:0;> *{vertical-align:top;}", " &[data-parent-id] ", ",&[data-parent-id] ", "{border-top:1px solid var(--border-color-dim);}", ""], _ref27 => {
  var {
    expanded,
    id
  } = _ref27;
  return css(["~ tr[data-parent-id='", "']{display:", ";}"], id, expanded ? 'table-row' : 'none');
}, DataColumnContainer, ActionColumn, _ref28 => {
  var {
    selectableCheckbox,
    selected
  } = _ref28;
  return selectableCheckbox && selected && css(["> td{background:var(--table-row-selected-color);}"]);
});
export var TableItemContainer = styled.tbody.withConfig({
  componentId: "CL__sc-l97mb9-19"
})([""]);

/* Outermost Table container */

export var OuterTableContainer = styled.div.withConfig({
  componentId: "CL__sc-l97mb9-20"
})(["position:relative;width:100%;", ";", ",", ",", ",", ",", "{", "}", ":nth-last-child(-n + 2){.tableActionDropdownButton + div > div{bottom:0;}}"], _ref29 => {
  var {
    cardWrapper,
    size
  } = _ref29;
  return cardWrapper && size ? "padding: ".concat(getTablePadding(size).vertical, " 0") : '';
}, TableLoadingState, TableEmptyState, TableControls, InnerTableContainer, TablePagination, _ref30 => {
  var {
    cardWrapper
  } = _ref30;
  return !cardWrapper && css(["padding-left:0;padding-right:0;"]);
}, StyledTableItem);